import { useMemo, useState } from 'react';
import { Props as ReactApexChartProps } from 'react-apexcharts';
import { Button } from '../../../../../../components/button/button';
import type { ExternalMonitoringPeriodBounds } from '../../../../../../helpers/external-monitoring-period';
import type { AggregationResultsCategoryRow } from '../../../../../../store/api/monitoring-api';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import {
  buildAspectsDynamicsByCategoryChartData,
  type AspectsDynamicsGroupingInterval,
  type AspectsDynamicsSentimentKey,
} from './build-aspects-dynamics-by-category-chart';
import { externalMonitoringLineChartInteractions } from './line-chart-interaction-options';
import s from './use-aspects-dynamics-by-category-line.module.css';

const EMPTY_ROWS: AggregationResultsCategoryRow[] = [];
const EMPTY_CATEGORY_NAMES: Record<number, string> = {};

export type AspectsDynamicsByCategoryInput = {
  rows: AggregationResultsCategoryRow[];
  categoryNamesById: Record<number, string>;
  groupingInterval: AspectsDynamicsGroupingInterval;
};

export const useAspectsDynamicsByCategoryLine = (
  _period?: ExternalMonitoringPeriodBounds,
  input?: AspectsDynamicsByCategoryInput,
): ChartProps => {
  const [sentiment, setSentiment] = useState<AspectsDynamicsSentimentKey>('negative');

  const rows = input?.rows ?? EMPTY_ROWS;
  const categoryNamesById = input?.categoryNamesById ?? EMPTY_CATEGORY_NAMES;
  const groupingInterval = input?.groupingInterval ?? 'day';

  const apexChartProps = useMemo(() => {
    const { xCategories, series } = buildAspectsDynamicsByCategoryChartData(
      rows,
      categoryNamesById,
      groupingInterval,
      sentiment,
    );

    const apex: ReactApexChartProps = {
      series,
      options: {
        chart: {
          type: 'line',
          ...externalMonitoringLineChartInteractions,
        },
        stroke: { curve: 'smooth', width: 2 },
        noData: {
          text: 'Нет данных за выбранный период',
          align: 'center',
        },
        xaxis: {
          categories: xCategories,
          title: { text: 'Период' },
        },
        yaxis: {
          title: { text: 'Количество комментариев' },
          labels: {
            formatter: (val: number) => String(Math.round(val)),
          },
        },
        legend: {
          position: 'bottom',
          onItemClick: {
            toggleDataSeries: true,
          },
          formatter: (seriesName: string, opts: { seriesIndex?: number }) => {
            const idx = opts.seriesIndex ?? 0;
            const ser = series[idx];
            const total = (ser?.data ?? []).reduce((a, v) => a + (Number(v) || 0), 0);
            return `${seriesName} — ${total}`;
          },
        },
        tooltip: {
          shared: true,
          y: {
            formatter: (val: number) => `${Math.round(val)} комментариев`,
          },
        },
      },
    };

    return apex;
  }, [rows, categoryNamesById, groupingInterval, sentiment, _period?.begin, _period?.end]);

  const footer = (
    <div className={s.footerRow}>
      <span className={s.toneLabel}>Тональность:</span>
      {(
        [
          { key: 'negative' as const, label: 'Негативно' },
          { key: 'neutral' as const, label: 'Нейтрально' },
          { key: 'positive' as const, label: 'Позитивно' },
        ] as const
      ).map((item) => (
        <Button
          key={item.key}
          type="button"
          variant="secondary"
          size="sm"
          outline
          className={item.key === sentiment ? s.toneBtnActive : undefined}
          onClick={() => setSentiment(item.key)}
        >
          {item.label}
        </Button>
      ))}
    </div>
  );

  return {
    ...apexChartProps,
    label: 'Динамика аспектов по категориям',
    labelTooltip:
      'На графике отображается изменение количества аспектов выбранной тональности (негативные, нейтральные или позитивные) в разрезе категорий за указанный период. Чтобы скрыть или отобразить отдельные категории, нажмите на их названия в легенде.',
    labelInfoIcon: true,
    type: 'line',
    footer,
  };
};
